import { Link, usePage } from "@inertiajs/react";
import { useState, useEffect, ReactElement } from "react";

import { DefaultPageType } from "types/DefaultPageType";

import CountBadge from "./ui/CountBadge";

function findIfPathMatch(
  href: string,
  currentPath: string,
  greedy: boolean | string,
) {
  if (!greedy) {
    return currentPath === href;
  }

  if (typeof greedy === "string") {
    return currentPath.startsWith(greedy);
  }

  return currentPath.startsWith(href);
}

type Props = {
  href: string;
  activeClassName: string;
  greedy?: boolean | string;
  exact?: boolean;
  className?: string;
  children: ReactElement;
  badge?: number | null;
  badgeClass?: string;
  pathname?: string;
};

export default function ActiveLink({
  href,
  activeClassName,
  children,
  className = "",
  greedy = false,
  exact = false,
  badge = null,
  badgeClass = "",
  pathname: initialPathname,
  ...props
}: Props) {
  const { pathName: pathNameWithoutQueryParams } =
    usePage<DefaultPageType>().props;
  const [active, setActive] = useState(
    findIfPathMatch(href, initialPathname, greedy),
  );

  const pathName = exact
    ? pathNameWithoutQueryParams + window.location.search
    : pathNameWithoutQueryParams;

  useEffect(() => {
    // Ex:
    //  href: "@hardcover/lists"
    //  currentPath: "@hardcover/lists/10-award-winning-books-by-asian-authors-you-should-read"
    const doesPathnameMatchLinkHref = findIfPathMatch(href, pathName, greedy);

    setActive(doesPathnameMatchLinkHref);
  }, [pathName, href, className, activeClassName, greedy]);

  return (
    <Link
      href={href}
      {...props}
      className={active ? activeClassName : className}
    >
      {children}

      {badge && (
        <CountBadge className={badgeClass} active={false} count={badge} />
      )}
    </Link>
  );
}
